import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { combineLatest, from, Observable, throwError } from 'rxjs';
import { first, shareReplay } from 'rxjs/operators';
import { Intent, PaymentMethodType, PaymentService, RetailProvider } from '@galaxy/billing';
import { Script, StripeService } from '../../stripe.service';
import { TranslateService } from '@ngx-translate/core';

declare let Stripe; // : stripe.StripeStatic;

@Component({
  selector: 'orders-payment-element-form',
  templateUrl: './payment-element-form.component.html',
  styleUrls: ['./payment-element-form.component.scss'],
  standalone: false,
})
export class PaymentElementFormComponent implements OnInit, AfterViewInit {
  @Input() userID?: string;
  @Input() stripeKey: string;
  @Input() retailProvider: RetailProvider;
  @Input({ required: true }) partnerId: string;
  @Input({ required: true }) accountGroupId: string;
  @Input() readOnly = false;

  @ViewChild('paymentElement') paymentElement: ElementRef;

  public stripeLoader$: Observable<Script>;

  stripe;
  payment;
  elements;
  error: string;
  paymentValid = false;
  paymentHandler = this.paymentChange.bind(this);

  paymentIntent$: Observable<Intent>;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private paymentService: PaymentService,
    private stripeService: StripeService,
    private readonly translate: TranslateService,
  ) {
    this.stripeLoader$ = this.stripeService.loadScript();
  }

  ngOnInit(): void {
    this.paymentIntent$ = this.paymentService
      .prepareRetailPayment(this.partnerId, this.accountGroupId, [PaymentMethodType.CARD])
      .pipe(shareReplay(1));
  }

  ngAfterViewInit(): void {
    combineLatest([this.paymentIntent$.pipe(first()), this.stripeLoader$.pipe(first())]).subscribe(([intent]) => {
      this.stripe = Stripe(this.stripeKey, {
        stripeAccount: this.retailProvider?.stripeConnectId,
      });
      this.elements = this.stripe.elements({
        clientSecret: intent.stripeClientSecret,
      });
      this.payment = this.elements.create('payment', {
        readOnly: this.readOnly,
        type: 'tabs',
        defaultCollapsed: false,
      });
      this.payment.mount(this.paymentElement.nativeElement);
      this.payment.addEventListener('change', this.paymentHandler);
    });
  }

  paymentChange({ complete, empty, error }): void {
    if (error) {
      this.error = error.message;
      this.paymentValid = false;
    } else if (empty && complete) {
      this.error = 'Your card number is incomplete.';
      this.paymentValid = false;
    } else if (complete) {
      this.error = '';
      this.paymentValid = true;
    } else {
      this.error = '';
      this.paymentValid = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  public focusCardInput(): void {
    this.payment.focus();
  }

  public getPaymentMethod(): Observable<string> {
    if (!this.paymentValid) {
      return throwError(this.translate.instant('LIB_ORDERS.COMMON.ORDERS.INVALID_CARD'));
    }
    const metadata = {};
    if (this.userID) {
      metadata['userID'] = this.userID;
    }
    return from<string>(
      this.stripe
        .confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: `${window.location.href}`,
            metadata,
          },
          redirect: 'if_required',
          metadata,
        })
        .then((result) => {
          return result?.setupIntent?.payment_method as string;
        }),
    );
  }
}
